import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { jwtDecode } from 'jwt-decode';
import html2canvas from 'html2canvas';
import { BASE_URL } from '../../utils/constants';
import NavigationUser from '../../Layouts/Navigation/NavigationUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faUsers,
  faCar,
  faMotorcycle,
  faCalendar,
  faClock,
  faFileLines,
  faCheckCircle,
  faTimesCircle,
  faQrcode,
  faShareNodes,
  faDownload,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

function VisitsControl() {
  const [token, setToken] = useState(null);
  const [open, setOpen] = useState(false);
  const qrBoxRef = useRef();
  const navigate = useNavigate();
  
  const [nombre, setName] = useState('');
  const [apellido, setSurname] = useState('');
  const [acompanantes, setCompanions] = useState(false);
  const [numeroAcompanantes, setNumCompanions] = useState(1);
  const [vehiculo, setVehicle] = useState(false);
  const [tipoVehiculo, setVehicleType] = useState('');
  const [placa, setPlate] = useState('');
  const [fechaVisita, setVisitDate] = useState('');
  const [horaVisita, setVisitTime] = useState('');
  const [nota, setNote] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success'
  });

  const [availableSpaces, setAvailableSpaces] = useState({
    carros: 0,
    motos: 0
  });
  const [loadingSpaces, setLoadingSpaces] = useState(false);

  const [InquilinoId, setInquilinoId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [qrSize, setQrSize] = useState(200);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setInquilinoId(decodedToken.id_usuario);
    } else {
      console.log('No hay token');
    }
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!InquilinoId) return;
      try {
        const resp = await axios.get(`${BASE_URL}/profile/${InquilinoId}`);
        setProfile(resp.data);
      } catch (err) {
        console.error('Error al obtener perfil:', err);
      }
    };
    fetchProfile();
  }, [InquilinoId]);

  // Ajustar tamaño del QR de forma responsiva
  useEffect(() => {
    const updateQrSize = () => {
      const vh = Math.min(window.innerWidth, window.innerHeight);
      const size = Math.min(200, Math.floor(vh * 0.45));
      setQrSize(size);
    };
    updateQrSize();
    window.addEventListener('resize', updateQrSize);
    return () => window.removeEventListener('resize', updateQrSize);
  }, []);

  useEffect(() => {
    if (vehiculo) {
      fetchAvailableSpaces();
    }
  }, [vehiculo]);

  const fetchAvailableSpaces = async () => {
    setLoadingSpaces(true);
    try {
      const response = await axios.get(`${BASE_URL}/visitorParking/stats`);
      setAvailableSpaces({
        carros: response.data.carros.disponibles || 0,
        motos: response.data.motos.disponibles || 0
      });
    } catch (error) {
      console.error('Error al obtener espacios:', error);
      showSnackbar('Error al verificar espacios de parqueadero', 'error');
    } finally {
      setLoadingSpaces(false);
    }
  };

  const generateToken = () => {
    const length = 6;
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  const validateDateTime = () => {
    if (!fechaVisita || !horaVisita) {
      setErrorMessage('Por favor selecciona fecha y hora de la visita');
      return false;
    }

    const selectedDateTime = new Date(`${fechaVisita}T${horaVisita}`);
    const now = new Date();

    if (selectedDateTime <= now) {
      setErrorMessage('La fecha y hora deben ser futuras');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const validateParking = () => {
    if (vehiculo && tipoVehiculo) {
      if (tipoVehiculo === 'CARRO' && availableSpaces.carros === 0) {
        showSnackbar('No hay espacios disponibles para carros', 'error');
        return false;
      }
      if (tipoVehiculo === 'MOTO' && availableSpaces.motos === 0) {
        showSnackbar('No hay espacios disponibles para motos', 'error');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateDateTime()) {
      showSnackbar(errorMessage, 'error');
      return;
    }

    if (!validateParking()) {
      return;
    }

    const enMora = profile?.estado_cartera === 'en_mora';
    if (vehiculo && enMora) {
      showSnackbar('Tu unidad tiene pagos pendientes. No puedes registrar visitas con vehículo hasta regularizar la cartera.', 'error');
      return;
    }

    const newToken = generateToken();
    setToken(newToken);

    const visitDateTime = `${fechaVisita} ${horaVisita}:00`;

    const visitData = {
      id_tipo_visita: 1,
      id_usuario: InquilinoId,
      nombre: nombre,
      apellido: apellido,
      num_acompanantes: acompanantes ? numeroAcompanantes : 0,
      tipo_vehiculo: vehiculo && tipoVehiculo ? tipoVehiculo : 'NO',
      placa: vehiculo && placa ? placa : 'NO',
      token: newToken,
      fecha: visitDateTime,
      observacion: nota || 'Sin observación',
      flag_ingreso: 0
    };

    try {
      const response = await axios.post(`${BASE_URL}/addVisit`, visitData);
      if (response.status === 200) {
        showSnackbar('Visita registrada con éxito', 'success');
        setOpen(true);
      }
    } catch (error) {
      console.error('Error al registrar visita:', error);
      const msg = error.response?.data?.message || 'Error al registrar la visita';
      showSnackbar(msg, 'error');
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset form
    setName('');
    setSurname('');
    setCompanions(false);
    setNumCompanions(1);
    setVehicle(false);
    setVehicleType('');
    setPlate('');
    setVisitDate('');
    setVisitTime('');
    setNote('');
    navigate('/userHome');
  };

  const handleDownload = () => {
    const qrBox = qrBoxRef.current;
    html2canvas(qrBox, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imgData;
      link.download = 'CodigoIngreso.png';
      link.click();
    });
  };

  const handleShare = () => {
    const qrBox = qrBoxRef.current;
    html2canvas(qrBox, { scale: 2 }).then((canvas) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], 'CodigoIngreso.png', { type: 'image/png' });
        if (navigator.share) {
          navigator.share({
            title: 'Código de Ingreso',
            files: [file],
          }).catch((error) => console.error('Error al compartir:', error));
        } else {
          console.error('La API Web Share no es compatible con este navegador.');
        }
      }, 'image/png', 1);
    });
  };

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
    setTimeout(() => {
      setSnackbar({ open: false, message: '', type: 'success' });
    }, 3000);
  };

  const validatePlate = (value) => {
    const regex = /^[A-Z]{3}-[0-9]{2}[0-9A-Z]$/;
    if (!regex.test(value) && value.length === 7) {
      setErrorMessage('Formato de placa inválido (Ej: ABC-123 o ABC-12D)');
    } else {
      setErrorMessage('');
    }
  };

  return (
    <>
      <NavigationUser />
      <div className="p-4 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-800 mb-1">
            Registrar Visita
          </h1>
          <p className="text-sm text-gray-600">
            Completa los datos de tu visitante para generar el código de acceso
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-4">
          {/* Datos del Visitante */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} className="h-5 w-5 text-indigo-600" />
              Datos del Visitante
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apellido *
                </label>
                <input
                  type="text"
                  value={apellido}
                  onChange={(e) => setSurname(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Acompañantes */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faUsers} className="h-5 w-5 text-green-600" />
              Acompañantes
            </h2>

            <div className="flex items-center gap-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acompanantes}
                  onChange={(e) => {
                    setCompanions(e.target.checked);
                    if (!e.target.checked) setNumCompanions(1);
                  }}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-gray-700 font-medium">¿Viene con acompañantes?</span>
              </label>
            </div>

            {acompanantes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Acompañantes *
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={numeroAcompanantes}
                  onChange={(e) => setNumCompanions(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Máximo 10 acompañantes</p>
              </div>
            )}
          </div>

          {/* Vehículo */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faCar} className="h-5 w-5 text-blue-600" />
              Vehículo
            </h2>

            {profile?.estado_cartera === 'en_mora' && (
              <div className="mb-3 bg-red-50 border border-red-400 rounded-lg p-3 flex items-start gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-red-800">Unidad con cartera en mora</p>
                  <p className="text-xs text-red-700 mt-0.5">Tu unidad tiene pagos pendientes con administración. No es posible registrar visitas con parqueadero hasta regularizar la cartera.</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 mb-3">
              <label className={`flex items-center gap-2 ${profile?.estado_cartera === 'en_mora' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={vehiculo}
                  disabled={profile?.estado_cartera === 'en_mora'}
                  onChange={(e) => {
                    setVehicle(e.target.checked);
                    if (!e.target.checked) {
                      setVehicleType('');
                      setPlate('');
                    }
                  }}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-gray-700 font-medium">¿Viene con vehículo?</span>
              </label>
            </div>

            {vehiculo && (
              <div className="space-y-3">
                {/* Disponibilidad de Espacios */}
                {loadingSpaces ? (
                  <div className="text-center py-3">
                    <p className="text-sm text-gray-600">Verificando disponibilidad...</p>
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-3 mb-3 border-2 border-gray-200">
                    <h3 className="text-base font-semibold text-gray-800 mb-2 text-center">
                      Disponibilidad de Parqueadero
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-lg p-3 shadow-sm border-2 border-blue-200">
                        <FontAwesomeIcon icon={faCar} className="h-8 w-8 mx-auto mb-1 text-blue-600" />
                        <p className="text-xs font-medium text-gray-600 text-center mb-1">Espacios Carros</p>
                        <p className={`text-3xl font-bold text-center ${availableSpaces.carros > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {availableSpaces.carros}
                        </p>
                        <p className="text-xs text-center mt-1 font-medium">
                          {availableSpaces.carros > 0 ? (
                            <span className="text-green-600">✓ Disponible</span>
                          ) : (
                            <span className="text-red-600">✗ No disponible</span>
                          )}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm border-2 border-green-200">
                        <FontAwesomeIcon icon={faMotorcycle} className="h-8 w-8 mx-auto mb-1 text-green-600" />
                        <p className="text-xs font-medium text-gray-600 text-center mb-1">Espacios Motos</p>
                        <p className={`text-3xl font-bold text-center ${availableSpaces.motos > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {availableSpaces.motos}
                        </p>
                        <p className="text-xs text-center mt-1 font-medium">
                          {availableSpaces.motos > 0 ? (
                            <span className="text-green-600">✓ Disponible</span>
                          ) : (
                            <span className="text-red-600">✗ No disponible</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Advertencia de disponibilidad */}
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">
                          <span className="font-semibold">Nota:</span> La disponibilidad de espacios puede cambiar si otras visitas llegan anticipadamente. Se recomienda verificar nuevamente antes de la hora programada.
                        </p>
                      </div>
                    </div>

                    {(availableSpaces.carros === 0 && availableSpaces.motos === 0) && (
                      <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-700 text-center font-medium">
                          ⚠️ No hay espacios de parqueadero disponibles en este momento
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tipo de Vehículo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Vehículo *
                  </label>
                  <div className="flex gap-3">
                    <label className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      tipoVehiculo === 'CARRO' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-blue-300'
                    }`}>
                      <input
                        type="radio"
                        value="CARRO"
                        checked={tipoVehiculo === 'CARRO'}
                        onChange={(e) => setVehicleType(e.target.value)}
                        className="sr-only"
                        disabled={availableSpaces.carros === 0}
                      />
                      <FontAwesomeIcon icon={faCar} className="h-6 w-6 text-blue-600" />
                      <span className="font-medium">Carro</span>
                      {(availableSpaces.carros === 0) && (
                        <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-500" />
                      )}
                    </label>

                    <label className={`flex-1 flex items-center justify-center gap-2 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      tipoVehiculo === 'MOTO' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-green-300'
                    }`}>
                      <input
                        type="radio"
                        value="MOTO"
                        checked={tipoVehiculo === 'MOTO'}
                        onChange={(e) => setVehicleType(e.target.value)}
                        className="sr-only"
                        disabled={availableSpaces.motos === 0}
                      />
                      <FontAwesomeIcon icon={faMotorcycle} className="h-6 w-6 text-green-600" />
                      <span className="font-medium">Moto</span>
                      {(availableSpaces.motos === 0) && (
                        <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-500" />
                      )}
                    </label>
                  </div>
                </div>

                {/* Placa */}
                {tipoVehiculo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placa del Vehículo *
                    </label>
                    <input
                      type="text"
                      value={placa}
                      onChange={(e) => {
                        let value = e.target.value.toUpperCase();
                        if (value.length === 3 && !value.includes('-')) {
                          value += '-';
                        }
                        if (value.length <= 7) {
                          setPlate(value);
                        }
                      }}
                      onBlur={(e) => validatePlate(e.target.value)}
                      placeholder="ABC-123 o ABC-12D"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      maxLength={7}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Formato: ABC-123 o ABC-12D</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fecha y Hora */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendar} className="h-5 w-5 text-purple-600" />
              Fecha y Hora de Llegada
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-3">
              <div className="flex">
                <FontAwesomeIcon icon={faClock} className="h-5 w-5 text-yellow-400 mr-2" />
                <div className="text-xs text-yellow-700">
                  <p className="font-medium">Importante:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>La hora de llegada debe ser posterior a la hora actual</li>
                    <li>El código es válido por 1 hora después de la hora seleccionada</li>
                    <li>El código es de un solo uso</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de Llegada *
                </label>
                <input
                  type="date"
                  value={fechaVisita}
                  onChange={(e) => setVisitDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hora de Llegada *
                </label>
                <input
                  type="time"
                  value={horaVisita}
                  onChange={(e) => setVisitTime(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Observaciones */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faFileLines} className="h-5 w-5 text-gray-600" />
              Observaciones (Opcional)
            </h2>

            <textarea
              value={nota}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Escribe cualquier observación adicional..."
              rows={3}
              maxLength={125}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {nota.length}/125 caracteres
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border-l-4 border-red-400 p-3">
              <div className="flex">
                <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center mt-4">
            <button
              type="submit"
              disabled={!!errorMessage}
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-2.5 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
            >
              <FontAwesomeIcon icon={faQrcode} className="h-5 w-5" />
              Generar Código de Acceso
            </button>
          </div>
        </form>

        {/* QR Code Dialog (responsive) */}
        {open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-full sm:max-w-md md:max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 rounded-t-lg">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="h-8 w-8" />
                  ¡Código Generado!
                </h2>
              </div>

              <div className="p-4 sm:p-6">
                <div ref={qrBoxRef} className="bg-white p-4 sm:p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    Código de Ingreso
                  </h3>
                  
                  {token && (
                    <div className="flex flex-col items-center gap-4">
                      <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                        <QRCodeSVG value={token} size={qrSize} />
                      </div>
                      
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-2">Código:</p>
                        <p className="text-3xl sm:text-4xl font-bold text-indigo-600 tracking-wider break-words">
                          {token}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 sm:mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800 font-medium">
                      ⏰ Este código es de un solo uso
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Válido por 1 hora después de la hora programada
                    </p>
                  </div>
                  {profile && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 text-left">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Destino</h4>
                      <p className="text-base font-bold text-gray-900">
                        {profile.torre_nombre || 'Sin torre'}{profile.num_apartamento ? ` - Apto ${profile.num_apartamento}` : ''}
                      </p>
                      <p className="text-sm text-gray-700 mt-1">{profile.nombre} {profile.apellido}</p>
                      {profile.telefono && (
                        <p className="text-sm text-blue-600 mt-1">
                          <a href={`tel:${profile.telefono}`}>{profile.telefono}</a>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-6">
                  <button
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faDownload} className="h-5 w-5" />
                    Descargar
                  </button>
                  <button
                    onClick={handleShare}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faShareNodes} className="h-5 w-5" />
                    Compartir
                  </button>
                </div>

                <button
                  onClick={handleClose}
                  className="w-full mt-3 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Snackbar */}
        {snackbar.open && (
          <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
            <div className={`flex items-center gap-3 px-6 py-3 rounded-lg shadow-lg ${
              snackbar.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}>
              {snackbar.type === 'success' ? (
                <FontAwesomeIcon icon={faCheckCircle} className="h-6 w-6" />
              ) : (
                <FontAwesomeIcon icon={faTimesCircle} className="h-6 w-6" />
              )}
              <span className="font-medium">{snackbar.message}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default VisitsControl;
